import { ChevronLeft, PackageCheck, RotateCcw, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const UPDATED_DATE = "12 August 2026";

function PolicySection({ number, title, children }) {
  return (
    <section className="scroll-mt-28">
      <h2 className="flex items-start gap-3 text-xl font-semibold tracking-tight text-slate-950 sm:text-2xl">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary-800 text-xs font-bold text-white">
          {number}
        </span>
        {title}
      </h2>

      <div className="mt-4 space-y-4 text-sm leading-7 text-slate-600 sm:pl-10 sm:text-[15px]">
        {children}
      </div>
    </section>
  );
}

export default function ReturnsRefundsPolicyPage() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen bg-[#fcfaf9] pb-16">
      <section className="border-b border-[#eadde1] bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 transition hover:text-primary-800"
          >
            <ChevronLeft size={17} />
            Back
          </button>

          <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-primary-800">
                <RotateCcw size={14} />
                Customer Care
              </div>

              <h1 className="mt-4 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
                Returns, Refunds
                <span className="block text-primary-800">& Exchanges</span>
              </h1>

              <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-600 sm:text-base">
                Our policy for eligible product issues, returns, refunds and
                exchanges from CHARMS stores and charmscollections.in.
              </p>
            </div>

            <div className="rounded-2xl border border-[#eadde1] bg-[#faf7f5] px-5 py-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-900">Last updated</p>
              <p className="mt-1">{UPDATED_DATE}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <aside className="h-fit rounded-2xl border border-[#eadde1] bg-white p-5 lg:sticky lg:top-48">
            <p className="text-xs font-bold uppercase tracking-[0.16em] text-primary-800">
              At a glance
            </p>

            <div className="mt-4 space-y-3 text-sm text-slate-600">
              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">24-hour reporting</p>
                <p className="mt-1 text-xs leading-5">
                  Report damaged, incorrect or defective items within 24 hours.
                </p>
              </div>

              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">Verification required</p>
                <p className="mt-1 text-xs leading-5">
                  Keep tags, packaging, invoice and clear photographs or video.
                </p>
              </div>

              <div className="rounded-xl bg-[#faf7f5] p-3">
                <p className="font-semibold text-slate-950">Easy exchanges</p>
                <p className="mt-1 text-xs leading-5">
                  Eligible exchanges may be settled in one combined bill.
                </p>
              </div>
            </div>
          </aside>

          <article className="rounded-3xl border border-[#eadde1] bg-white p-6 shadow-sm sm:p-10">
            <div className="mb-10 rounded-2xl border border-primary-100 bg-primary-50 p-5">
              <div className="flex gap-3">
                <ShieldCheck className="mt-0.5 shrink-0 text-primary-800" size={21} />

                <p className="text-sm leading-6 text-primary-950">
                  This policy applies to purchases made through
                  <span className="font-semibold"> charmscollections.in</span> and,
                  where stated at the relevant store, CHARMS retail points of sale.
                  Nothing in this policy limits rights that cannot lawfully be
                  excluded under applicable consumer law.
                </p>
              </div>
            </div>

            <div className="space-y-10">
              <PolicySection
                number="1"
                title="When a Return or Exchange May Be Requested"
              >
                <p>
                  Because our products are branded apparel and may include
                  hygiene-sensitive or limited-availability items, returns are not
                  offered simply because of a change of mind, preference, colour
                  choice, fit choice or a later price change, unless required by law
                  or expressly approved by CHARMS.
                </p>

                <p>
                  You may request a review if an item is delivered damaged, has a
                  manufacturing defect, is incorrect, has material missing parts, or
                  is materially different from the item you ordered.
                </p>

                <p>
                  Store exchanges may also be considered for an eligible item where
                  stock is available and the request is approved.
                </p>
              </PolicySection>

              <PolicySection number="2" title="Reporting a Problem">
                <p>
                  Please report the issue within <strong>24 hours</strong> of
                  delivery or purchase. Include your order or receipt number, a clear
                  description, photographs or video where relevant, and keep the
                  item, original tags, packaging and invoice available for
                  inspection.
                </p>

                <p>
                  For a damaged parcel or apparent transit issue, please record the
                  condition at delivery where practical and notify us promptly. A
                  valid claim may still require inspection before any remedy is
                  approved.
                </p>
              </PolicySection>

              <PolicySection number="3" title="Eligibility Conditions">
                <ul className="space-y-3">
                  {[
                    "The item must be unused, unwashed, unworn and free from perfume, deodorant, alterations, stains or other signs of use, unless the defect itself prevents this.",
                    "Original tags, labels, packaging, accessories, invoice and proof of purchase must be available where reasonably possible.",
                    "The requested quantity must not exceed the quantity actually purchased and not previously returned.",
                    "The item must pass our verification process. We may ask for additional evidence or arrange an inspection or reverse pickup.",
                    "Items sold as final sale, customised items, gift cards, personal-care items or other excluded goods may not be eligible except where required by law.",
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary-800" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </PolicySection>

              <PolicySection number="4" title="Our Review and Available Remedies">
                <p>
                  After reviewing an eligible request, CHARMS may, as appropriate and
                  subject to stock availability and applicable law, offer a
                  replacement, size exchange, repair where appropriate, refund, store
                  credit or restoration of loyalty points.
                </p>

                <p>
                  The appropriate remedy will depend on the nature of the issue, the
                  original payment method, the purchase channel and applicable law.
                </p>

                <p>
                  Where a purchase was paid wholly or partly with loyalty points, any
                  approved reversal, return or exchange will take account of points
                  originally redeemed and points originally earned on the returned
                  portion. Points earned on returned merchandise may be reversed.
                </p>

                <p>
                  If points were used to pay for an eligible returned portion,
                  CHARMS may restore the corresponding eligible points rather than
                  issue cash for that points-funded amount, subject to applicable law.
                </p>
              </PolicySection>

              <PolicySection number="5" title="Refund Method and Timing">
                <p>
                  Once a refund is approved and the item is received or otherwise
                  verified, we will initiate the applicable refund or credit. Refunds
                  required by law will be made to the original payment method unless
                  another lawful method is agreed.
                </p>

                <p>
                  Processing time depends on the payment provider, bank, gateway,
                  logistics verification and any required inspection. We will share
                  the expected refund timeline when your return is approved.
                </p>

                <p>
                  For point-of-sale returns, the original receipt is helpful but not
                  always required. CHARMS may use barcode, item and transaction
                  records to verify an eligible purchase and returnable quantity
                  before accepting a return or exchange.
                </p>
              </PolicySection>

              <PolicySection number="6" title="Exchanges and Combined Exchange Bills">
                <p>
                  An approved exchange can include a returned item and one or more
                  new items in a single settlement. If the replacement items cost
                  more, the customer must pay the difference using an available
                  approved method.
                </p>

                <p>
                  If the approved return value is more, CHARMS will provide the
                  eligible balance through the approved refund, store-credit or
                  loyalty process.
                </p>

                <div className="rounded-2xl border border-[#eadde1] bg-[#faf7f5] p-5">
                  <div className="flex gap-3">
                    <PackageCheck
                      className="mt-0.5 shrink-0 text-primary-800"
                      size={21}
                    />

                    <p className="text-sm leading-6 text-slate-600">
                      Return and sale values are recorded separately for audit, tax
                      and inventory purposes, even when settled together in one
                      exchange bill.
                    </p>
                  </div>
                </div>
              </PolicySection>

              <PolicySection number="7" title="Rejected or Abusive Claims">
                <p>
                  We may decline a request where eligibility is not established, the
                  item is used or altered, evidence is inconsistent, the request is
                  outside the stated window, or there is suspected fraud or misuse.
                </p>

                <p>
                  Any decision will not affect rights available under applicable law.
                </p>
              </PolicySection>

              <PolicySection number="8" title="Contact Us">
                <p>
                  For an eligible return, exchange, refund query or grievance,
                  contact CHARMS Customer Support with your order number or POS
                  receipt number.
                </p>

                <div className="rounded-2xl bg-[#faf7f5] p-5 text-sm">
                  <p>
                    <strong className="text-slate-950">Customer Support:</strong>{" "}
                    support@charmscollections.in
                  </p>

                  <p className="mt-2">
                    <strong className="text-slate-950">Website:</strong>{" "}
                    charmscollections.in
                  </p>

                  <p className="mt-2 text-slate-500">
                    For formal grievances, contact the grievance officer details
                    displayed in the CHARMS Privacy Policy.
                  </p>
                </div>
              </PolicySection>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
}